import React, { createElement } from 'react';
import { Spin } from 'antd';
import pathToRegexp from 'path-to-regexp';
import Loadable from 'react-loadable';
import { getUserPermission } from 'components/PermissionCheck/PermissionCheck';
import { getUserRole } from 'src/utils/tenantContext';
import { PredefinedRole } from 'src/services/types/cedar/role';
import { getMenuData } from './menu';
import { useTenantsContext } from '../common/gobalState';

let routerDataCache;

const modelNotExisted = (app, model) =>
  // eslint-disable-next-line
  !app._models.some(({ namespace }) => {
    return namespace === model.substring(model.lastIndexOf('/') + 1);
  });

// wrapper of dynamic
const dynamicWrapper = (app, models, component) => {
  // register models
  models.forEach((model) => {
    if (modelNotExisted(app, model)) {
      // eslint-disable-next-line
      app.model(require(`../models/${model}`).default);
    }
  });

  // transformed by babel-plugin-dynamic-import-node-sync
  if (component.toString().indexOf('.then(') < 0) {
    return (props) => {
      if (!routerDataCache) {
        routerDataCache = getRouterData(app);
      }
      return createElement(component().default, {
        ...props,
        routerData: routerDataCache,
      });
    };
  }

  return Loadable({
    loader: () => {
      if (!routerDataCache) {
        routerDataCache = getRouterData(app);
      }
      if (!navigator.onLine) {
        window.location.reload(false);
        return null;
      }
      return component().then((raw) => {
        const Component = raw.default || raw;
        return (props) =>
          createElement(Component, {
            ...props,
            routerData: routerDataCache,
          });
      });
    },
    loading: () => {
      return <Spin size="large" className="global-spin" />;
    },
  });
};

function getFlatMenuData(menus) {
  let keys = {};
  menus.forEach((item) => {
    if (item.children) {
      keys[item.path] = { ...item };
      keys = { ...keys, ...getFlatMenuData(item.children) };
    } else {
      keys[item.path] = { ...item };
    }
  });
  return keys;
}

export const getRouterData = (app) => {
  const routerConfig = {};

  // 修改现有的addPermissionRoute函数，增加identification参数
  const addPermissionRoute = (path, models, comp, permissionCode, name, identification) => {
    if (!permissionCode || getUserPermission()[permissionCode]) {
      // 检查内外部访问控制
      const [tenants] = useTenantsContext();
      const internalPerson = tenants?.internalPerson;
      
      // 外部（!internalPerson）账号不能访问（identification给了INTERNAL）跳转403
      const shouldRedirect403 = (identification === 'INTERNAL' && !internalPerson)
      
      routerConfig[path] = {
        name: name,
        component: shouldRedirect403 
          ? dynamicWrapper(app, [], () => import('../routes/Exception/403'))
          : dynamicWrapper(app, models, comp),
        platform: 'app',
        identification: identification,
      };
    }
  };

  addPermissionRoute(
    '/robot/manager',
    ['robot', 'login'],
    () => import('../routes/Robot/List/ListWrapper'),
    'Robot.Manager.view',
  );

  // addPermissionRoute(
  //   '/robot/upper-computer-backup-log',
  //   ['robot', 'login'],
  //   () => import('../routes/Robot/UpperComputerBackup/Log/Log'),
  //   'Robot.UpperComputerBackup.view',
  //   i('上位机备份日志'),
  // );

  // addPermissionRoute(
  //   '/robot/upper-computer-backup-detail/:sn?',
  //   ['robot', 'login'],
  //   () => import('../routes/Robot/UpperComputerBackup/Detail/Detail'),
  //   'Robot.UpperComputerBackup.view',
  //   i('上位机备份详情'),
  // );


  // addPermissionRoute(
  //   '/robot/upper-computer-backup',
  //   ['robot', 'login'],
  //   () => import('../routes/Robot/UpperComputerBackup/UpperComputerBackup'),
  //   'Robot.UpperComputerBackup.view',
  //   i('上位机备份'),
  // );

  addPermissionRoute(
    '/backup-restore/robot-backup',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'BackupAndRecovery.MachineBackup.view',
  );

  addPermissionRoute(
    '/backup-restore/robot-backup-detail/:sn?',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'BackupAndRecovery.MachineBackup.view',
  );

  addPermissionRoute(
    '/backup-restore/robot-backup-detail/:sn/restore-backup',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'BackupAndRecovery.MachineBackup.view',
  );

  addPermissionRoute(
    '/backup-restore/robot-backup-log',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'BackupAndRecovery.MachineBackup.OperationLog',
  );

  addPermissionRoute(
    '/backup-restore/recovery-plan',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'BackupAndRecovery.RecoveryPlanList.view',
  );

  // addPermissionRoute(
  //   '/robot/oneclick-switch',
  //   ['robot', 'login'],
  //   () => import('../routes/Robot/OneclickSwitch/OneClickSwitch'),
  //   'Robot.MigrationPlan.view',
  //   i('一键换机'),
  // );

  // addPermissionRoute(
  //   '/robot/oneclick-switch-plan/planlist',
  //   ['robot', 'login'],
  //   () => import('../routes/Robot/OneclickSwitch/PlanList'),
  //   'Robot.MigrationPlan.view',
  //   i('一键换机'),
  // );

  addPermissionRoute(
    '/schedule/task/detail',
    ['robot', 'login'],
    () => import('../routes/MicroApp/MicroApp'),
    'Robot.Manager.Detail.SchedulingTask.view',
  );

  addPermissionRoute(
    '/robotDeploy/laserActive',
    [],
    () => import('../routes/RobotDeploy/LaserActive'),
    'Deploy.LaserActivation.view',
    i('激光激活'),
  );

  addPermissionRoute(
    '/robot/detail/:id/:curModule?/:name?',
    ['robot', 'login'],
    () => import('../routes/Robot/Detail/Detail'),
    'Robot.Manager.Detail.view',
  );

  addPermissionRoute(
    '/robot/old/detail/:id/:curModule?/:name?',
    ['robot', 'login'],
    () => import('../routes/Robot/Detail/OldVersionTheRobotDetail/Detail'),
    'Robot.Manager.Detail.view',
  );

  addPermissionRoute(
    '/map/mapEdit/:mapId/:mapVersion/:readonly?',
    ['robot', 'login'],
    () => import('../routes/MicroApp/MicroApp'),
    'MapManager.List.EditVersion.view',
    i('地图编辑')
  );

  addPermissionRoute(
    '/robot/task-report/:id',
    ['robot', 'download'],
    () => import('../routes/Robot/TaskReport/TaskReport'),
    'Robot.Manager.Detail.DataOverview.Task.view',
  );

  addPermissionRoute(
    '/robot/real-time/:id',
    ['robot', 'login'],
    () => import('../routes/Robot/Realtime/Realtime'),
    'Robot.Manager.Detail.ConnectButtonShow.RealTimeViewButtonShow.view',
    i('实时视图'),
  );

  addPermissionRoute(
    '/robot/old/real-time/:id',
    ['robot', 'login'],
    () => import('../routes/Robot/Detail/OldVersionTheRobotDetail/RealTime/Realtime'),
    'Robot.Manager.Detail.ConnectButtonShow.RealTimeViewButtonShow.view',
    i('实时视图'),
  );

  // addPermissionRoute(
  //   '/robot/robot-status/laser/:id',
  //   ['robot', 'login'],
  //   () => import('../routes/Robot/RobotStatus/RobotStatusLaser'),
  //   'Robot.Manager.Detail.ConnectButtonShow.SensorDataButtonShow.view',
  //   i('传感数据'),
  // );

  // addPermissionRoute(
  //   '/robot/old/robot-status/laser/:id',
  //   ['robot', 'login'],
  //   () => import('../routes/Robot/Detail/OldVersionTheRobotDetail/RobotStatus/RobotStatusLaser'),
  //   'Robot.Manager.Detail.ConnectButtonShow.SensorDataButtonShow.view',
  //   i('传感数据'),
  // );

  addPermissionRoute(
    '/robot/task/:robotSn?',
    ['robot', 'download'],
    () => import('../routes/TaskManager/TaskManager'),
    'Robot.Task.view',
    i('任务管理'),
  );

  addPermissionRoute(
    '/robot/shared-record-history',
    ['robot', 'login'],
    () => import('../routes/DataCenter/SharedRecord/SharedRecordHistory/SharedRecordHistory'),
    'Robot.Manager.view',
    i('记录分享历史'),
    'INTERNAL' // 添加内部人员限制
  );

  addPermissionRoute(
    '/robot/shared-record-list/:id/:sn?',
    ['robot', 'login'],
    () => import('../routes/DataCenter/SharedRecord/SharedRecordList/SharedRecordList'),
    'Robot.view',
    i('记录列表'),
    'INTERNAL' // 添加内部人员限制
  );


  addPermissionRoute(
    '/event/template-content/:pageType/:operationType/:id?',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'EventCenter.view',
  );

  addPermissionRoute(
    '/event/robot-config/:operationType/:id?',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'EventCenter.EventSubscribe.view',
    i('机器人配置表'),
  );

  addPermissionRoute(
    '/event/new-event-analysis',
    [],
    () => import("../routes/MicroApp/MicroApp"),
    'EventCenter.AnalysisExternal.view',
    i('事件分析'),
  );

  addPermissionRoute(
    '/map/list',
    ['robot', 'login'],
    () => import('../routes/MicroApp/MicroApp'),
    'MapManager.List.view',
    i('地图列表'),
  );

  addPermissionRoute(
    '/map/mapEdit/:mapId/:mapVersion/:readonly?',
    ['robot', 'login'],
    () => import('../routes/MicroApp/MicroApp'),
    'MapManager.List.EditVersion.view',
    i('地图编辑')
  );

  addPermissionRoute(
    '/map/detail/:id',
    ['robot', 'login'],
    () => import('../routes/MicroApp/MicroApp'),
    'MapManager.List.view',
    i('地图详情'),
  );

  addPermissionRoute(
    '/map/issued-record',
    ['robot', 'login'],
    () => import('../routes/MicroApp/MicroApp'),
    'MapManager.TransmissionList.view',
    i('下发记录'),
  );

  addPermissionRoute(
    '/map/edit-record',
    ['robot', 'login'],
    () => import('../routes/MicroApp/MicroApp'),
    'MapManager.EditRecord.view',
    i('编辑记录'),
  );
  
  addPermissionRoute(
    '/upgrade/firmware-manager/:name?',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Upgrade.FirmwareManager.view',
    i('升级管理/版本管理')
  );
  addPermissionRoute(
    '/upgrade/firmware-manager/detail/:name?',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Upgrade.FirmwareManager.view',//todo
    i('升级管理/版本管理/固件详情')
  );
  addPermissionRoute(
    '/upgrade/firmware-history/:title/:deviceScope/bundles/:name/:state',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Upgrade.FirmwareManager.view',//todo
    i('升级管理/版本管理/升级历史')
  );
  addPermissionRoute(
    '/upgrade/robot-upgrade/:sn?',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Upgrade.RobotUpgrade.view',
    i('机器人升级')
  );
  addPermissionRoute(
    '/upgrade/firmware-push-plan',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Upgrade.FirmwarePushPlan.view',
    i('版本推送计划')
  );
  addPermissionRoute(
    '/upgrade/upgrade-history/:sn?/:upgradeTrigger?',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Upgrade.History.view',
    i('升级管理/升级历史')
  );
  addPermissionRoute(
    '/upgrade/upgrade-log',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Upgrade.Log.view',
    i('升级管理/升级事件日志')
  );

  addPermissionRoute(
    '/upgrade-service/robot-upgrade-service',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'ExternalUpgradeService.RobotUpgrade.view',
    i('机器人升级')
  );

  addPermissionRoute(
    '/upgrade-service/push-manager-service',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'ExternalUpgradeService.FirmwarePushPlan.view',
    i('版本推送计划')
  );

  addPermissionRoute(
    '/dataPlatform/coreSparePartBoard-Summary',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.coreSparePartBoard-Summary',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/coreSparePartKanbanA',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.coreSparePartKanbanA',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/coreSparePartKanbanB',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.coreSparePartKanbanB',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/coreSparePartKanbanC',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.coreSparePartKanbanC',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/coreSparePartKanbanX',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.coreSparePartKanbanX',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/coreSparePartKanbanY',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.coreSparePartKanbanY',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/coreSparePartKanbanZ',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.coreSparePartKanbanZ',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/coreSparePartKanban-English',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.coreSparePartKanban-English',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/faultTroubleShooting-Korean',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.faultTroubleShooting-Korean',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/faultTroubleShooting-Japanese',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.faultTroubleShooting-Japanese',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/faultTroubleShooting-English',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.faultTroubleShooting-English',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/faultTroubleShooting-Chinese',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.faultTroubleShooting-Chinese',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/robotUsageDataDashboard-English',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.robotUsageDataDashboard-English',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/robotUsageDataDashboard-Chinese',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.robotUsageDataDashboard-Chinese',
    i('数据平台')
  );

  addPermissionRoute(
    '/dataPlatform/robotUsageDataDashboard-Japanese',
    [],
    () => import("../routes/DataPlatform"),
    'dataPlatform.robotUsageDataDashboard-Japanese',
    i('数据平台')
  );


  // addPermissionRoute(
  //   '/rule-center/GenerateRules-manager',
  //   ['robot', 'login'],
  //   () => import("../routes/MicroApp/MicroApp"),
  //   'Rule.GenerateRulesManager',
  //   '生成规则',
  // );

  // addPermissionRoute(
  //   '/rule-center/GenerateRules-manager/create',
  //   ['robot', 'login'],
  //   () => import("../routes/MicroApp/MicroApp"),
  //   'Rule.GenerateRulesManager',
  //   '生成规则新增',
  // );

  // addPermissionRoute(
  //   '/rule-center/GenerateRules-manager/edit/:id',
  //   ['robot', 'login'],
  //   () => import("../routes/MicroApp/MicroApp"),
  //   'Rule.GenerateRulesManager',
  //   '生成规则编辑',
  // );

  // addPermissionRoute(
  //   '/rule-center/GenerateRules-manager/view/:id',
  //   ['robot', 'login'],
  //   () => import("../routes/MicroApp/MicroApp"),
  //   'Rule.GenerateRulesManager',
  //   '生成规则查看',
  // );

  addPermissionRoute(
    '/rule-center/incidentGroup-manager',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Rule.IncidentGroupManager',
    i('事件组'),
  );

  addPermissionRoute(
    '/rule-center/incidentGroup-manager/create',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Rule.IncidentGroupManager',
    i('事件组新增'),
  );

  addPermissionRoute(
    '/rule-center/incidentGroup-manager/edit/incidentGroups/:id',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Rule.IncidentGroupManager',
    i('事件组编辑'),
  );

  addPermissionRoute(
    '/rule-center/incidentGroup-manager/view/incidentGroups/:id',
    ['robot', 'login'],
    () => import("../routes/MicroApp/MicroApp"),
    'Rule.IncidentGroupManager',
    i('事件组查看'),
  );

//2023-12-12新增内外部页面控制，路由注册新增identification字段，
// 值为 INTERNAL_EXTERNAL 时代表内外部都可访问页面，
// 值为 INTERNAL 时代表仅内部可见，
// 值为 EXTERNAL 时代表仅外部可见

  // 非权限路由
  const assertRouter = {
    '/': {
      component: dynamicWrapper(app, ['login'], () => import('../layouts/BasicLayout')),
    },
    '/exception/404': {
      component: dynamicWrapper(app, [], () => import('../routes/Exception/404')),
    },
    '/utils/dev': {
      component: dynamicWrapper(app, ['login'], () => import('../components/_utils/JSONView')),
    },
    '/components/MoveControl': {
      component: dynamicWrapper(app, ['login'], () => import('../components/MoveControl/MoveControl')),
    },
    '/robot/robot-status/sonar/:id': {
      component: dynamicWrapper(app, ['robot', 'login'], () => import('../routes/Robot/RobotStatus/RobotStatusSonar')),
    },
    '/robot/robot-status/protector/:id': {
      component: dynamicWrapper(app, ['robot', 'login'], () =>
        import('../routes/Robot/RobotStatus/RobotStatusProtector'),
      ),
    },
    '/exception/403': {
      component: dynamicWrapper(app, [], () => import('../routes/Exception/403')),
    },
    '/exception/500': {
      component: dynamicWrapper(app, [], () => import('../routes/Exception/500')),
    },
    '/personal-data': {
      name: i('个人中心'),
      component: dynamicWrapper(app, [], () => import('../routes/MicroApp/MicroApp')),
    },
    '/download-center': {
      name: i('下载中心'),
      component: dynamicWrapper(app, [], () => import('../routes/MicroApp/MicroApp')),
    }
  };
  addPermissionRoute(
    '/robot/:robotExplain?',
    ['robot', 'login'],
    () => import('../routes/Robot/List/ListWrapper'),
    'Robot.Manager.view',
  );
  const allRouter = Object.assign(routerConfig, assertRouter);
  // Get name from ./menu.js or just set it in the router data.
  const menuData = getFlatMenuData(getMenuData());

  // Route configuration data
  // eg. {name,authority ...routerConfig }
  const routerData = {};
  // The route matches the menu
  Object.keys(allRouter).forEach((path) => {
    // Regular match item name
    // eg.  router /user/:id === /user/chen
    const pathRegexp = pathToRegexp(path);
    const menuKey = Object.keys(menuData).find((key) => pathRegexp.test(`${key}`));
    let menuItem = {};
    // If menuKey is not empty
    if (menuKey) {
      menuItem = menuData[menuKey];
    }
    let router = allRouter[path];
    // If you need to configure complex parameter routing,
    // https://github.com/ant-design/ant-design-pro-site/blob/master/docs/router-and-nav.md#%E5%B8%A6%E5%8F%82%E6%95%B0%E7%9A%84%E8%B7%AF%E7%94%B1%E8%8F%9C%E5%8D%95
    // eg . /list/:type/user/info/:id
    router = {
      ...router,
      name: router.name || menuItem.name,
      authority: router.authority || menuItem.authority,
      hideInBreadcrumb: router.hideInBreadcrumb || menuItem.hideInBreadcrumb,
    };
    routerData[path] = router;
  });
  return routerData;
};
